/*
 * The administrator's first day (VISUAL.md §7.16, D-10 §2.4): on an instance whose only account is
 * the administrator's, the checklist *is* the first screen — not a card behind a greeting. The
 * heading says what the screen is for, the rows say what is left, the progress bar says how far the
 * place has come, and every row ends in a door.
 *
 * What the screen decides for itself is small: the greeting (by the first name, like Home's) and the
 * dismissal — every door writes `core.shell.seen_welcome` on the way out, as the member's screen
 * does, and *Got it* does the same without going anywhere. The card around it, with the picture and
 * that foot, is `FirstRunCard.tsx`, which the member's screen wears too. The checklist itself is
 * `SetupChecklist.tsx`, which also stands on Home afterwards for as long as the instance is new, so
 * an administrator who took one door still finds the other three.
 *
 * The first row's door comes from a module (`HomeSection.SetupDoor`): creating the channel everybody
 * joins is chat's to do, and the step-up a stale session is asked for appears inside that door,
 * never in a dialog over this screen (D-10 D11).
 */

import { t } from "../i18n/index.ts"
import { setupDoorOf } from "../modules/registry.ts"
import { useSlot } from "../modules/slots.ts"
import { useModel } from "./context.tsx"
import { firstName } from "./firstName.ts"
import { FirstRunCard } from "./FirstRunCard.tsx"
import { useShellSetting } from "./preferences.tsx"
import { SetupChecklist, type PlaceDoor } from "./SetupChecklist.tsx"

export function FirstRunAdmin({
	channel,
	compact = false,
}: {
	channel?: PlaceDoor | undefined
	/** The block above the channel list on a narrow window: no illustration, a small mark beside the heading. */
	compact?: boolean
}) {
	const [, setSeen] = useShellSetting("core.shell.seen_welcome")
	const name = useModel((model) => firstName(model.me?.displayName ?? ""))
	const SetupDoor = setupDoorOf(useSlot("homeSections"))
	const dismiss = () => setSeen(true)

	return (
		<FirstRunCard
			title={t("shell.firstRun.admin.title", { name })}
			lead={t("shell.firstRun.admin.lead")}
			compact={compact}
			onDismiss={dismiss}
		>
			<SetupChecklist channel={channel} SetupDoor={SetupDoor} onDoor={dismiss} headed={false} />
		</FirstRunCard>
	)
}
